import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  Link,
  Alert,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Select,
  MenuItem,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useLogin, useNotify } from 'react-admin';

export const RegisterPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [userRole, setUserRole] = useState('driver');
  const [companyName, setCompanyName] = useState('');
  const [emailPreference, setEmailPreference] = useState('all');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const login = useLogin();
  const notify = useNotify();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('invitationId');
  const isInviteFlow = Boolean(invitationId);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!firstName.trim()) {
      setError('First name is required');
      return;
    }

    if (!lastName.trim()) {
      setError('Last name is required');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    const effectiveRole = isInviteFlow ? 'driver' : userRole;

    // Validate company name if company role is selected
    if (effectiveRole === 'company' && !companyName.trim()) {
      setError('Company name is required when selecting Company role');
      return;
    }

    setLoading(true);

    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;

      // Register the user
      // For now, both driver and owner-operator are treated as operator role
      // since we don't have support for the new userRole field yet
      const apiKey = import.meta.env.VITE_API_KEY;
      const response = await fetch(`${baseUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(apiKey && { 'x-api-key': apiKey }),
        },
        body: JSON.stringify({
          email,
          password,
          firstName,
          lastName,
          emailPreference,
          userRole:
            effectiveRole === 'owner-operator' ? 'owner_operator' : effectiveRole,
          companyName: effectiveRole === 'company' ? companyName.trim() : undefined,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Registration failed');
      }

      const data = await response.json();

      // Auto-login after successful registration
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);
      
      // Fetch user with tenants to ensure tenant data is available (important for company users)
      try {
        const userResponse = await fetch(`${baseUrl}/users/${data.user.id}`, {
          headers: {
            'Authorization': `Bearer ${data.accessToken}`,
          },
        });
        
        if (userResponse.ok) {
          const userWithTenants = await userResponse.json();
          localStorage.setItem('user', JSON.stringify(userWithTenants));
          console.log('User with tenants loaded:', userWithTenants);
          
          // For company users, set the current tenant in localStorage
          if (effectiveRole === 'company' && userWithTenants.tenants && userWithTenants.tenants.length > 0) {
            const tenant = userWithTenants.tenants[0];
            localStorage.setItem('currentTenant', JSON.stringify({
              code: tenant.tenantCode,
              name: tenant.tenant?.name || 'Company Tenant'
            }));
            console.log('Set current tenant for company user:', tenant.tenantCode);
          }
        } else {
          // Fallback to user from registration if fetch fails
          localStorage.setItem('user', JSON.stringify(data.user));
        }
      } catch (fetchError) {
        console.warn('Failed to fetch user with tenants, using user from registration:', fetchError);
        localStorage.setItem('user', JSON.stringify(data.user));
      }

      // Store registration data for setup wizard (especially company name for company users)
      if (effectiveRole === 'company' && companyName) {
        localStorage.setItem('registrationData', JSON.stringify({ companyName: companyName.trim() }));
      }

      // Store registration flag to trigger setup wizard
      localStorage.setItem('showSetupWizard', 'true');

      // Redirect to setup wizard
      notify('Registration successful! Let\'s finish setting up your account.', { type: 'success' });
      window.location.href = '/setup-wizard';
    } catch (err: any) {
      setError(err.message || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
        backgroundColor: '#3b4d66',
        paddingTop: '10vh',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '48px 40px',
          backgroundColor: 'white',
          borderRadius: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: 450,
        }}
      >
        <img
          src="/logo-dv-full-text.png"
          alt="DV Logo"
          style={{
            height: '40px',
            marginBottom: '24px',
          }}
        />

        <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', textAlign: 'center' }}>
          Create your account
        </Typography>

        {error && (
          <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            width: '100%',
          }}
        >
          {/* Name Fields */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="First name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              required
              fullWidth
              autoComplete="given-name"
            />
            <TextField
              label="Last name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              required
              fullWidth
              autoComplete="family-name"
            />
          </Box>

          <TextField
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            autoComplete="email"
          />

          <TextField
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
            helperText="Minimum 8 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    aria-label="toggle password visibility"
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            label="Confirm password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            fullWidth
            autoComplete="new-password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    aria-label="toggle confirm password visibility"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* User Role Section — hidden for users arriving from a tenant invitation */}
          {!isInviteFlow && (
          <FormControl sx={{ mt: 2 }}>
            <FormLabel sx={{ fontWeight: 'bold', mb: 2 }}>How are you involved in the driving industry?</FormLabel>
            <RadioGroup value={userRole} onChange={(e) => setUserRole(e.target.value)}>
              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  value="driver"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Driver:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        You do not own a vehicle and work for a company or owner-operator who provides you with work and vehicle(s) to use.
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  value="owner-operator"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Owner-operator:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        You own at least one vehicle and operate as an independent contractor or under a company name.
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  value="company"
                  control={<Radio />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        Company:
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                        You represent a commercial transportation company that employs drivers and operates vehicles.
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </RadioGroup>
          </FormControl>
          )}

          {/* Company Name Field - shown only when company role is selected */}
          {!isInviteFlow && userRole === 'company' && (
            <TextField
              label="Company Name"
              type="text"
              value={companyName}
              onChange={(e) => {
                setCompanyName(e.target.value);
                setError('');
              }}
              required
              fullWidth
              placeholder="Enter your company name"
              helperText="This will be used to create your company profile"
            />
          )}

          {/* Email Preference */}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <FormLabel sx={{ mb: 1 }}>
              May we occasionally send you helpful e-mails? This includes DriverVerified news, educational and informational content, and relevant services you might find interesting.
            </FormLabel>
            <Select
              value={emailPreference}
              onChange={(e) => setEmailPreference(e.target.value)}
              size="small"
            >
              <MenuItem value="all">Yes, send all emails</MenuItem>
              <MenuItem value="important">Only important notifications</MenuItem>
              <MenuItem value="none">No, don't send me emails</MenuItem>
            </Select>
          </FormControl>

          {/* Terms Agreement */}
          <Typography variant="caption" sx={{ mt: 2, color: 'text.secondary', textAlign: 'center' }}>
            By clicking "Create Account" below, you are confirming that you have read and agree to the{' '}
            <Link href="#" underline="hover" sx={{ fontWeight: 'bold' }}>
              DriverVerified Terms of Service
            </Link>
            .
          </Typography>

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading}
            sx={{ mt: 2, py: 1.5, backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#1565c0' } }}
          >
            {loading ? 'Creating Account...' : 'Create account'}
          </Button>

          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Already have an account?{' '}
              <Link
                href="/login"
                underline="hover"
                sx={{ fontWeight: 'bold', cursor: 'pointer' }}
              >
                Sign In
              </Link>
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
